import { useMemo } from "react";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { useChildForm } from "@/features/leads/contexts/ChildProvider";
import useLeadPipeline from "@/features/leads/hook/useLeadPipeline";
import { stageCanHoldDeals } from "@/features/stage/utils/stageCanHoldDeals";
import useModuleByIndustry from "@/hooks/api/useModuleByIndustry";
import NicheAttachmentSlot from "./NicheAttachmentSlot";
import { type ModuleConnector } from "./nicheRegistry";

/**
 * Renders one slot per `child`-targeted ModuleConnecter for the current industry.
 * Each slot shows either:
 *  - the registered AttachedView (when attached + niche is registered)
 *  - an "Attach <label>" CTA (when not attached + niche is registered)
 *  - a generic ScopeViewRendererCard fallback (when attached + niche not registered)
 *  - nothing (when not attached + niche not registered)
 *

 * TWO subjects (owner, 2026-10-15: "if child allowed stage, just show the
 * attach things; if attached then create child, else don't ever automatically
 * create child" — and, 2026-10-16, on a lead in the pipeline's FIRST stage
 * with no slot in sight: "show attach thing all the time… if attached then it
 * will be the first deal"):
 *  - the ACTIVE DEAL, as before — attaching patches that deal;
 *  - a lead with NO deal yet, in ANY stage of a pipeline that has Choices
 *    enabled somewhere — the same slots render in CREATE mode and attaching
 *    creates the lead's first deal carrying the picked item. The deal lands
 *    in the lead's own stage when that stage holds deals, else in the
 *    pipeline's first Choices stage (the server's `ChildCreation` rule); the
 *    slot says which. No stage move ever makes one.
 *
 * 100% data-driven from the server's ModuleConnecter rows (seeded via
 * DEFAULT_INDUSTRIES on the server). NEVER branches on industry.id.
 */
export default function ActiveChildModuleAttachments({
  /** False = the "Deal Module Attachments" display toggle is off: hide the
   *  attached-deal cards, but never the create slot (it is the only way to
   *  get a first deal, and a hidden slot read as "the feature is missing"). */
  showAttached = true,
}: {
  showAttached?: boolean;
} = {}) {
  const { child, isChildActive, lead } = useActiveDataMutation();
  const { allChildren, allChildrenLoading } = useChildForm();
  const { pipeline } = useLeadPipeline();
  const { modules, isLoading } = useModuleByIndustry();

  type PipelineStage = { id?: number | string; name?: string; order?: number; isChild?: boolean };
  const stages = useMemo<PipelineStage[]>(() => (Array.isArray(pipeline) ? (pipeline as PipelineStage[]) : []), [pipeline]);

  const childConnectors = useMemo<ModuleConnector[]>(() => {
    if (!Array.isArray(modules)) return [];
    return modules.filter((m: ModuleConnector) => m?.coreModelName === "child");
  }, [modules]);

  // Create mode: no deal at all on this lead (the deal list has loaded and is
  // empty) and the lead's pipeline has Choices enabled SOMEWHERE. Where the
  // first deal lands: the lead's own stage when it holds deals (flagged
  // Choices or at / after the first Choices stage — `stageCanHoldDeals`),
  // else the first Choices stage. The pipeline list is the drawer's
  // (branch-aware) one, so a forked branch's flags apply.
  const leadStageId = Number(lead?.stageId ?? lead?.stage?.id);
  const dealHome = useMemo<{ enabled: boolean; stageId: number | null; stageName: string | null; atOwnStage: boolean }>(() => {
    const none = { enabled: false, stageId: null, stageName: null, atOwnStage: false };
    if (stages.length === 0) return none;
    const choices = stages.filter((s) => Boolean(s?.isChild)).sort((a, b) => Number(a.order) - Number(b.order));
    if (choices.length === 0) return none;
    const own = stages.find((s) => Number(s?.id) === leadStageId);
    const atOwnStage =
      !!own && stageCanHoldDeals(stages as ReadonlyArray<{ order: number; isChild?: boolean }>, Number(own.order), Boolean(own.isChild));
    const home = atOwnStage ? own : choices[0];
    return {
      enabled: true,
      atOwnStage,
      stageId: Number.isFinite(Number(home?.id)) ? Number(home?.id) : null,
      stageName: String(home?.name ?? ""),
    };
  }, [stages, leadStageId]);
  const createMode =
    !isChildActive &&
    !!lead?.id &&
    !allChildrenLoading &&
    (allChildren?.length ?? 0) === 0 &&
    dealHome.enabled;

  if (!createMode && (!isChildActive || !child || !showAttached)) return null;

  // Connector list still loading on first paint → render nothing to avoid layout jump.
  if (isLoading && childConnectors.length === 0) return null;

  // Industry has no child-attached niches at all → render nothing.
  if (childConnectors.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      {childConnectors.map((connector) => (
        <NicheAttachmentSlot
          key={connector.id}
          child={createMode ? null : child}
          lead={createMode ? lead : undefined}
          connector={connector}
          createStageId={createMode ? dealHome.stageId : null}
          createStageName={createMode && !dealHome.atOwnStage ? dealHome.stageName : null}
        />
      ))}
    </div>
  );
}
